import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
    Grid,
    Tooltip
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import {
    BreadCrumbs,
    PageTitleWithToolTip,
    DeleteModal,
    DataGrid,
    AddUserButton
} from "../Common";


import { AppRoles, trakrefFontFamily } from "../../utils";
import Routes from "../../routes";
import UserInfo from "../../UserInfo";

import UserAddBulkModal from "./UserAddBulkModal";
import { useNavigate, useBreadCrumbs } from "../../hooks";
import { StyledCellLink } from "../Common/DataGrid/CellComponents";
import { select_commonCellProps, text_commonCellProps, EmptyActionIcon } from "../Common/DataGrid/utils";
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { Grey2 } from "../../color_utils";

const Users = () => {
    const [filters, setFilters] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [openBulkModal, setOpenBulkModal] = useState(false);
    const [refreshNeeded, setRefreshNeeded] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState({
        id: 0,
        visibility: false,
    });

    const navigate = useNavigate({
        pageName: 'Users'
    });
    const breadCrumbs = useBreadCrumbs({
        path: { id: 'Users', text: 'Manage Users', link: window.location.pathname }
    });

    const canManageGroup = UserInfo.isRoleExist(AppRoles.Can_Manage_User);

    const onButtonMenuChangeState = () => setIsOpen((prev) => !prev);

    const onDeleteUser = async () => {
        if (UserInfo.userId === showDeleteModal.id) {
            return;
        }

        try {
            const response = await fetch(Routes.User.DeleteUser(showDeleteModal.id), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });
            const data = await response.json();

            if (response.status !== 200 || !data) {
                return;
            }

            setRefreshNeeded((prev) => !prev);
            setShowDeleteModal({ id: 0, visibility: false });
            toast.success('The user has been successfully deleted', { duration: 6000 });
        } catch {
            console.error("something went wrong!");
        }
    };

    const fetchTableFilterOptions = useCallback(async () => {
        const response = await fetch(Routes.Account.UserTableFilters());
        const data = await response.json();

        const newData = [
            {
                property: "certificationType",
                name: "None",
                value: "None"
            },
            ...data,
            {
                property: "userStatus",
                value: "Active",
                name: "Active"
            },
            {
                property: "userStatus",
                value: "Deactivated",
                name: "Deactivated"
            },
            {
                property: "userStatus",
                value: "Deleted",
                name: "Deleted"
            }
        ];

        setFilters(newData);
    }, []);

    useEffect(() => {
        fetchTableFilterOptions();
    }, [fetchTableFilterOptions]);


    const columns = [
        {
            field: 'id',
            headerName: 'User ID',
            ...text_commonCellProps,
            initiallyVisible: false,
        },
        {
            field: 'name',
            headerName: 'Name',
            ...text_commonCellProps,
            renderCell: ({ row }) => {
                return <StyledCellLink
                    path={`/User/${row.id}`}
                    text={row.name}
                    from={window.location.pathname}
                    enabled={row.canViewUsers}
                    primaryLink
                />
            }
        },
        {
            field: 'userName',
            headerName: 'User Name',
            ...text_commonCellProps
        },
        {
            field: 'company',
            headerName: 'Company',
            ...text_commonCellProps
        },
        {
            field: 'userStatus',
            headerName: 'Status',
            ...select_commonCellProps
        },
        {
            field: 'email',
            headerName: 'Email Address',
            ...text_commonCellProps
        },
        {
            field: 'state',
            headerName: 'State',
            ...select_commonCellProps
        },
        {
            field: 'country',
            headerName: 'Country',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'type',
            headerName: 'Type',
            ...select_commonCellProps
        },
        {
            field: 'certificationType',
            headerName: 'Certification Type',
            ...select_commonCellProps
        },
        {
            field: 'certificationNumber',
            headerName: 'Certification Number',
            ...text_commonCellProps
        },
        {
            type: 'actions',
            field: 'Quick Actions',
            width: 100,
            getActions: ({ row: { id, canViewUsers, canEditUser, canRemoveUser } }) => [
                canViewUsers ? (
                    <GridActionsCellItem
                        key="view"
                        icon={
                            <Tooltip title="View User">
                                <SearchIcon sx={{ color: Grey2 }} />
                            </Tooltip>
                        }
                        onClick={() => navigate.push(`/User/${id}`)}
                    />
                ) : <EmptyActionIcon />,
                canEditUser ? (
                    <GridActionsCellItem
                        key="edit"
                        icon={
                            <Tooltip title="Edit User">
                                <EditIcon sx={{ color: Grey2 }} />
                            </Tooltip>
                        }
                        onClick={() => navigate.push(`/UserUpdate/${id}`)}
                    />
                ) : <EmptyActionIcon />,
                canRemoveUser ? (
                    <GridActionsCellItem
                        key="remove"
                        icon={
                            <Tooltip title="Delete User">
                                <DeleteIcon sx={{ color: Grey2 }} />
                            </Tooltip>
                        }
                        onClick={() => setShowDeleteModal({ id, visibility: true })}
                    />
                ) : <EmptyActionIcon />
            ].filter(Boolean)
        }
    ];

    return (
        <>
            <BreadCrumbs
                path={breadCrumbs.getSortedBreadCrumbs()}
            />
            <Grid container direction="row" alignItems="center">
                <Grid item xs={6} md={6} lg={6}>
                    <PageTitleWithToolTip pageTitle={"Manage Users"} toolTipText={"Add new users and manage existing users."} />
                </Grid>
                <Grid item xs={6} md={6} lg={6} align="right">
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            flexDirection: "row",
                            gap: "15px",
                        }}
                    >
                        {
                            canManageGroup && (
                                <AddUserButton
                                    openMenu={isOpen}
                                    history={navigate}
                                    onOpen={onButtonMenuChangeState}
                                    onClose={onButtonMenuChangeState}
                                />
                            )
                        }
                    </div>
                </Grid>
                <Grid item xs={12} sx={{ marginTop: "15px" }}>
                    <DataGrid
                        url={Routes.Account.UserTable()}
                        exportButtonURL={Routes.Account.UserTableExport()}
                        exportButtonAlt={"Export Users (.csv)"}
                        hideExportButton={!UserInfo.isSystemAdministrator() && !UserInfo.isNativeUser()}
                        filterOptions={filters}
                        refresh={refreshNeeded}
                        noResultText="Oops...It is empty here..."
                        noResultText2="Please create a new user to get started"
                        noResultText3="Create a new user or change your search criterias."
                        columns={columns}
                    />
                </Grid>
            </Grid >

            <UserAddBulkModal
                modalOpen={openBulkModal}
                onClose={() => setOpenBulkModal(false)}
            />

            <DeleteModal
                open={showDeleteModal.visibility}
                buttonText={"Yes, Delete"}
                title="Delete this user?"
                description="It will be permanently lost."
                onClose={() => setShowDeleteModal({ id: 0, visibility: false })}
                onConfirm={onDeleteUser}
            />
        </>
    );
};

export default Users;
